<template>
  <div
    ref="tableRect"
    class="table-wrapper"
  >
    <div class="table-header">
      <b-button
        v-if="$can('create', 'certificates', 'cdn')"
        variant="bg-primary"
        class="mb-1"
        @click="$router.push({ name: 'certificates-request' })"
      >
        Request a certificate
      </b-button>
      <b-button
        v-if="$can('create', 'certificates', 'cdn')"
        variant="bg-primary"
        class="mb-1"
        @click="onCreate()"
      >
        <GjIcon
          class="icon-left"
          name="Upload"
        />
        Import SSL Certificate
      </b-button>
      <b-button
        v-if="$can('get', 'certificates', 'cdn')"
        variant="bg-white"
        class="mb-1 d-none d-lg-inline"
        :disabled="!isSelected"
        @click="onDetailsClick(selected[0])"
      >
        Details
      </b-button>
      <button-loading
        v-if="$can('delete', 'certificates', 'cdn')"
        variant="bg-white"
        class="mb-1 d-none d-lg-inline"
        :disabled="!isSelected"
        :spinning="removingCertificate"
        @click="onDeleteClick"
      >
        Delete
      </button-loading>
    </div>

    <div
      class="position-relative"
      :class="
        currentBreakPoint === 'lg' || currentBreakPoint === 'xl'
          ? 'default-table'
          : 'mt-2'
      "
    >
      <b-table
        class="d-none d-lg-block"
        :items="certificates"
        :fields="fields"
        :class="{ loaded: !loading }"
        select-mode="single"
        show-empty
        selectable
        responsive
        @row-selected="onRowSelected"
      >
        <template #cell(selected)="{ rowSelected }">
          <select-button :selected="rowSelected" />
        </template>

        <template #cell(id)="data">
          <a
            class="clickable"
            @click="onDetailsClick(data.item)"
          >{{
            data.value
          }}</a>
        </template>

        <template #cell(scope)="data">
          <div class="d-flex align-items-center">
            {{ data.value }}
            <a
              class="ml-auto"
              @click="edit($event, data.item)"
            >
              <GjIcon
                name="Edit"
                class="m-0"
                size="17"
              />
            </a>
          </div>
        </template>

        <template #cell(certificateStatus)="data">
          <div
            :class="{
              'text-danger text-darken-2 d-flex align-items-center':
                data.value !== 'Issued' && data.value !== 'Renewed',
            }"
          >
            <span>{{ data.value }}</span>
            <b-dropdown
              v-if="data.value !== 'Issued' && data.value !== 'Renewed'"
              variant="transparent"
              class="actions-dropdown"
              no-caret
            >
              <template #button-content>
                <GjIcon
                  name="Moreoptions"
                  class="m-0"
                  color="#d7212a"
                />
              </template>
              <b-dropdown-item @click="validateCertificate(data.item.id)">
                Validate
              </b-dropdown-item>
            </b-dropdown>
          </div>
        </template>

        <template #cell(lastModified)="data">
          {{ formatOffsetDateTime(data.value) }}
        </template>
        <template #cell(syncOnCdn77)="data">
          <b-form-checkbox
            :checked="data.item.syncOnCdn77"
            switch
            :disabled="syncingCertificate"
            @change="onSyncToggle(data.item, $event)"
          />
        </template>

        <template #empty>
          <div
            v-if="loading"
            class="loading-table text-center py-3"
          >
            <b-spinner variant="primary" />
          </div>
          <template v-else>
            <no-data
              no-data-text="No certificates have been added yet..."
              create-text="+ Create Certificate"
              access-page="Certificates"
              icon="Security"
              :has-permissions="hasPermissions"
              @action="onCreate()"
            />
          </template>
        </template>
      </b-table>
      <table-busy v-if="loading && certificates.length > 0" />

      <div class="d-lg-none">
        <div
          :class="{ 'opacity-7': loading }"
          class="overflow-hidden"
        >
          <template v-for="certificate in certificates">
            <card
              :key="certificate.id"
              :fields="fields"
              :item="certificate"
              class="top-fade-in"
            >
              <template #options>
                <b-dropdown
                  id="dropdown-1"
                  right
                  variant="white"
                  @show="select(certificate)"
                  @hide="select(null)"
                >
                  <template #button-content>
                    <GjIcon name="Moreoptions" />
                  </template>
                  <b-dropdown-item
                    v-if="$can('get', 'certificates', 'cdn')"
                    @click="onDetailsClick(certificate)"
                  >
                    Details
                  </b-dropdown-item>
                  <b-dropdown-item
                    v-if="$can('delete', 'certificates', 'cdn')"
                    @click="onDeleteClick"
                  >Delete</b-dropdown-item>
                </b-dropdown>
              </template>
              <template #id>
                <a
                  class="link"
                  @click="onDetailsClick(certificate)"
                >{{
                  certificate.id
                }}</a>
              </template>
              <template #certificateStatus>
                <div
                  :class="{
                    'text-danger text-darken-2 d-flex align-items-center':
                      certificate.certificateStatus !== 'Issued' &&
                      certificate.certificateStatus !== 'Renewed',
                  }"
                >
                  <span>{{ certificate.certificateStatus }}</span>
                  <b-dropdown
                    v-if="
                      certificate.certificateStatus !== 'Issued' &&
                        certificate.certificateStatus !== 'Renewed'
                    "
                    variant="white"
                    class="actions-dropdown"
                    no-caret
                  >
                    <template #button-content>
                      <GjIcon
                        name="Moreoptions"
                        class="m-0"
                        color="#d7212a"
                      />
                    </template>
                    <b-dropdown-item
                      @click="validateCertificate(certificate.id)"
                    >
                      Validate
                    </b-dropdown-item>
                  </b-dropdown>
                </div>
              </template>
              <template #scope>
                <div class="w-100">
                  {{ certificate.scope }}
                  <a
                    class="ml-1"
                    @click="edit($event, certificate)"
                  >
                    <GjIcon
                      name="Edit"
                      class="m-0"
                      size="17"
                    />
                  </a>
                </div>
              </template>
              <template #lastModified>
                {{ formatOffsetDateTime(certificate.lastModified) }}
              </template>
              <template #syncOnCdn77>
                <b-form-checkbox
                  :checked="certificate.syncOnCdn77"
                  switch
                  :disabled="syncingCertificate"
                  @change="onSyncToggle(certificate, $event)"
                />
              </template>
            </card>
          </template>
        </div>
        <div
          v-if="loading"
          class="mobile-loading"
        >
          <table-busy />
        </div>
        <template v-if="!loading && certificates.length < 1">
          <paper-simple class="top-fade-in">
            <no-data
              class="text-center"
              no-data-text="No certificates have been added yet..."
              create-text="+ Create Certificate"
              access-page="Certificates"
              icon="Security"
              :has-permissions="hasPermissions"
              @action="onCreate()"
            />
          </paper-simple>
        </template>
      </div>

      <div
        v-if="certificates.length > 0"
        class="default-pagination"
      >
        <b-pagination
          v-model="currentPage"
          :total-rows="totalCount"
          :per-page="pageSize"
          first-number
          last-number
        />
        <div class="results">
          Viewing {{ first.toLocaleString() }} to {{ last.toLocaleString() }} of {{ totalCount.toLocaleString() }} Items
        </div>
      </div>
    </div>
    <edit-scope
      v-if="item !== null"
      :item="item"
      :something-wrong="cantGetCertificate"
      :rect="rect"
      :table-rect="tableRect"
      @close="item = null"
    />
  </div>
</template>
<script>
import Card from '@/components/card/Card.vue'
import EditScope from '../helpers/EditScope.vue'

export default {
  name: 'Certificates',
  components: {
    Card,
    EditScope,
  },
  data() {
    return {
      selected: {},
      fields: [
        {
          key: 'selected',
          label: '',
          variant: 'select',
          hide: true,
        },
        { key: 'id', variant: 'select', isPrimary: true },
        { key: 'domainName', isPrimary: true },
        { key: 'certificateStatus', tdClass: 'file-actions validate' },
        'certificateType',
        { key: 'scope', tdClass: 'file-actions' },
        { key: 'syncOnCdn77', label: 'Sync with CDN77' },
        'lastModified',
      ],
      id: 0,
      certificateId: null,
      hasPermissions: true,
      cantGetCertificate: false,
      rect: null,
      item: null,
    }
  },
  computed: {
    certificates() {
      return this.$store.state.cdn.certificates
    },
    row() {
      return this.selected[0] ? this.selected[0] : {}
    },
    isSelected() {
      return this.selected[0]
    },
    removingCertificate() {
      return this.$store.state.cdn.removingCertificate
    },
    syncingCertificate() {
      return this.$store.state.cdn.syncingCertificate
    },
    loading() {
      return this.$store.state.cdn.fetchingCertificates
    },
    resultInfo() {
      return this.$store.state.cdn.certificatesResultInfo
    },
    totalCount() {
      return this.resultInfo.totalCount
    },
    pageSize() {
      return this.resultInfo.pageSize || 10
    },
    pageIndex() {
      return this.currentPage - 1
    },
    first() {
      return this.pageIndex * this.pageSize + 1
    },
    last() {
      return this.currentPage * this.pageSize < this.totalCount
        ? this.currentPage * this.pageSize
        : this.totalCount
    },
    currentBreakPoint() {
      return this.$store.getters['app/currentBreakPoint']
    },
    currentPage: {
      get() {
        return this.$store.getters['cdn/certificatesCurrentPage']
      },
      set(val) {
        this.$store.commit('cdn/SET_CERTIFICATES_CURRENT_PAGE', val)
      },
    },
    tableRect() {
      return this.$refs.tableRect.getBoundingClientRect()
    },
  },
  watch: {
    currentPage() {
      this.fetchCertificates()
    },
  },
  created() {
    if (this.$store.state.app.previousRouteName !== 'certificates-view') {
      this.currentPage = 1
    }
    this.fetchCertificates()
    this.$store.dispatch('cdn/getCertificate', 0).catch(() => {
      this.cantGetCertificate = true
    })
  },
  methods: {
    select(certificate) {
      this.selected = certificate !== null ? [certificate] : []
    },
    onRowSelected(item) {
      this.selected = item
    },
    fetchCertificates() {
      this.$store
        .dispatch('cdn/fetchCertificates', {
          filter: false,
          pageSize: this.pageSize,
          pageIndex: this.pageIndex,
        })
        .catch(error => {
          if (error.response?.status === 403) {
            this.hasPermissions = false
            return
          }
          this.errorToast(
            error.response?.data?.errors[0]
              || 'Something went wrong while fetching certificates.',
          )
        })
    },
    onCreate() {
      this.$router.push({
        name: 'certificates-create',
        params: { id: this.id },
      })
    },
    onDeleteClick() {
      this.confirmDelete(
        'Delete Certificate',
        'Are you sure you want to delete this certificate?',
      ).then(ok => {
        if (ok) {
          this.$store
            .dispatch('cdn/removeCertificate', this.selected[0].id)
            .then(() => {
              this.successToast('Certificate was deleted')
              if (this.certificates.length === 0 && this.currentPage > 1) {
                this.currentPage = 1
              } else {
                this.fetchCertificates()
              }
            })
            .catch(error => {
              this.errorToast(
                error.response?.data?.errors[0] || 'Something went wrong.',
              )
            })
        }
      })
    },
    onDetailsClick(data) {
      this.$router.push({
        name: 'certificates-view',
        params: {
          distributionId: this.$route.params.id,
          certificateId: data.id,
        },
      })
    },
    validateCertificate(id) {
      this.$store
        .dispatch('cdn/validateCertificate', id)
        .then(() => this.successToast('Certificate status was updated.'))
        .catch(error => {
          this.errorToast(
            error.response?.data?.errors[0]
              || 'Something went wrong while updating certificate status.',
          )
        })
    },
    edit(e, item) {
      e.stopPropagation()
      this.rect = e.target.getBoundingClientRect()
      this.item = item
    },
    onSyncToggle(item, value) {
      const previous = item.syncOnCdn77
      item.syncOnCdn77 = value
      this.$store
        .dispatch('cdn/syncCertificate', { id: item.id, sync: value })
        .then(() => this.successToast('CDN77 sync was updated.'))
        .catch(error => {
          item.syncOnCdn77 = previous
          this.errorToast(
            error.response?.data?.errors[0]
              || 'Something went wrong while updating CDN77 sync.',
          )
        })
    },
  },
}
</script>
<style lang="scss" scoped>
.table-wrapper {
  .table-header {
    margin-top: 0;
  }
  .default-table {
    margin-top: 12px;
    .background-transparent {
      background-color: transparent !important;
    }
  }
}
.default-table::v-deep {
  .file-actions {
    overflow: visible;
    &.validate {
      height: 58px;
    }
  }
}

.actions-dropdown::v-deep {
  .dropdown-toggle {
    height: auto;
    padding: 0;
  }
}
</style>
